/**
 * Threat Intelligence tab.
 *
 * Two source-of-truth sections kept from the old "Threat Intel Upload" tab:
 *
 *   • Upload IOC File   — drag-drop CSV/TXT/JSON, parsed and pushed to
 *                         Microsoft Sentinel Threat Intelligence.
 *   • Manual IOC Entry  — paste-list with the same submit path.
 *
 * Two new sections wired to the Rösti (Repackaged Open Source Threat
 * Intelligence) public feed via our /api/rosti/* proxy:
 *
 *   • Latest Threat Intel Reports — paged list of recent reports with
 *                                   source, date, IOC counts and a deep-link
 *                                   to the original write-up.
 *   • Threat Hunt Results        — automatic 30-day Sentinel `search` of the
 *                                   active customer's Log Analytics workspace
 *                                   for any IOC that turned up in the last
 *                                   batch of Rösti reports.
 */
import { useState, useRef, useEffect, useCallback, type ReactElement } from 'react';
import { getApiBase } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';
import kloudynetLogo from '@assets/image_1777160759976.png';
import { fmtTs } from '../lib/globalTz';

interface RostiReport {
  id: string;
  title: string;
  url: string;
  date: string;
  last_updated?: string;
  authors?: string[] | null;
  source?: { id?: string; name?: string; url?: string };
  tags?: string[];
  count?: { iocs?: number; yara_rules?: number; mitre_ids?: number; cve?: number };
}

interface RostiReportsResponse {
  data: RostiReport[];
  meta?: { total?: number; count?: number };
  cached?: boolean;
  fetchedAt?: string;
  error?: string;
  detail?: string;
}

interface HuntMatch {
  /** Earliest TimeGenerated across deduped occurrences (== firstSeen). */
  timestamp: string;
  table: string;
  matchedIocs: string[];
  raw: Record<string, unknown>;
  /** How many raw rows collapsed into this distinct event. */
  occurrences?: number;
  firstSeen?: string;
  lastSeen?: string;
}

interface SuppressedIoc {
  value: string;
  type: string;
  /** MISP list slug (filename), e.g. "rfc1918". */
  list: string;
  /** Human-readable list name from the MISP JSON. */
  listName: string;
}

interface SuppressionSummary {
  totalSuppressed: number;
  byList: { list: string; listName: string; count: number }[];
  /** First N suppressed IOCs (server-side cap) — for the audit drawer. */
  sample: SuppressedIoc[];
}

/** A row from the per-customer FP suppression list, as returned by
 *  GET /rosti/hunt/suppressions and inserted by POST. */
interface HuntSuppression {
  id: string;
  customerId: string;
  iocValue: string;
  iocType: string;
  reason: string | null;
  suppressedBy: string | null;
  createdAt: string;
}

interface CustomerSuppressedIoc {
  value: string;
  type: string;
  reason?: string;
}

interface CustomerSuppressionSummary {
  totalSuppressed: number;
  /** First N suppressed IOCs, for the audit drawer. */
  sample: CustomerSuppressedIoc[];
}

interface HuntResult {
  customerId: string;
  workspaceId: string;
  workspaceName?: string;
  lookbackDays: number;
  iocsHunted: number;
  iocsByType: Record<string, number>;
  /** MISP warninglist pre-filter audit. Optional for backwards compat. */
  suppressedByWarninglist?: SuppressionSummary;
  /** Per-customer analyst-curated FP suppression audit. */
  suppressedByCustomerList?: CustomerSuppressionSummary;
  /** True when the analyst chose to disable the MISP warninglist filter
   *  for this hunt. The customer FP list is ALWAYS applied. */
  warninglistsBypassed?: boolean;
  /** Distinct (deduped) event count — what the analyst should triage. */
  totalHits: number;
  /** Total raw row count returned by Sentinel before dedupe. */
  rawHits?: number;
  /** Distinct events per table (deduped). */
  hitsByTable: Record<string, number>;
  /** Raw row count per table before dedupe. */
  rawHitsByTable?: Record<string, number>;
  hitsByIoc: Record<string, number>;
  matches: HuntMatch[];
  topReports: { id: string; title: string; url: string; iocCount: number }[];
  truncated: boolean;
  ranAt: string;
  durationMs: number;
  /** Oldest huntable IOC's added-to-feed timestamp (ISO). */
  iocOldestSeen?: string;
  /** Days of Rösti feed coverage represented by the hunted IOC set. */
  iocCoverageDays?: number;
  error?: string;
  cached?: boolean;
}

type IocActionKind =
  | 'email_received'
  | 'url_in_email'
  | 'url_clicked'
  | 'file_written'
  | 'process_executed'
  | 'image_loaded'
  | 'network_call'
  | 'device_event'
  | 'perimeter_seen'
  | 'dns_query'
  | 'sign_in'
  | 'saas_activity'
  | 'windows_event'
  | 'other';

interface IocAction {
  kind: IocActionKind;
  timestamp: string;
  table: string;
  summary: string;
  user?: string;
  device?: string;
}

interface IocTimeline {
  firstSeen: string;
  lastSeen: string;
  durationHours: number;
  activeDays: number;
  peakDay?: { date: string; hits: number };
}

interface IocRootCause {
  ioc: string;
  vector:
    | 'Phishing'
    | 'Drive-by Compromise'
    | 'Exploit Public-Facing Application'
    | 'External Remote Services'
    | 'Valid Accounts'
    | 'Supply Chain Compromise'
    | 'Trusted Relationship'
    | 'Replication Through Removable Media'
    | 'Lateral Movement'
    | 'Command and Control'
    | 'Unknown';
  confidence: 'Low' | 'Medium' | 'High';
  narrative: string;
  evidence: string[];
  timeline: IocTimeline;
  actions: IocAction[];
  tablesPresent: string[];
}

interface HuntNarrative {
  verdict: 'CLEAN' | 'INVESTIGATE' | 'ACTIVE_THREAT';
  headline: string;
  summary: string;
  keyFindings: string[];
  affectedDevices: string[];
  affectedUsers: string[];
  recommendations: string[];
  rootCauseAnalysis: IocRootCause[];
}

interface HuntNarrativeResponse {
  narrative: HuntNarrative;
  customerId: string;
  lookbackDays: number;
  huntRanAt: string;
  generatedAt: string;
  error?: string;
  kind?: string;
}

function timeAgo(iso: string | undefined | null): string {
  if (!iso) return 'unknown';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'unknown';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export function IocManagementTab(): ReactElement {
  const { activeCustomerId, customers } = useCustomers();
  const activeCustomer = customers.find(c => c.id === activeCustomerId);
  const { canWrite } = useAuth();
  // /rosti/hunt is gated by `requireTabWrite("threat_intel")` server-side
  // because it spends real Sentinel query units. The customer_viewer portal
  // role typically has visible/read but not write on threat_intel, so we
  // hide the Re-run button and skip the auto-run on mount instead of
  // letting the POST 403 with "forbidden". Hunts for portal users are
  // refreshed by their SOC team.
  const canRunHunt = canWrite('threat_intel', activeCustomerId);

  // ── Rösti reports state ──────────────────────────────────────────────────
  const [reports, setReports] = useState<RostiReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [reportsFetchedAt, setReportsFetchedAt] = useState<string | null>(null);

  // ── Hunt state ───────────────────────────────────────────────────────────
  const [hunt, setHunt] = useState<HuntResult | null>(null);
  const [huntLoading, setHuntLoading] = useState(false);
  const [huntError, setHuntError] = useState<string | null>(null);
  // Sentinel lookback window. Anything in [1, 90] is accepted server-side;
  // 30 keeps the long-tail-IOC visibility we had before the dropdown.
  const [lookbackDays, setLookbackDays] = useState<number>(30);
  // Analyst override: skip MISP warninglist pre-filter for this hunt.
  // Persisted only in component state (not URL/localStorage) — it should
  // be a deliberate per-hunt decision, not a sticky default. Audit-logged
  // server-side every time it's set to true.
  const [bypassWarninglists, setBypassWarninglists] = useState<boolean>(false);
  // Per-customer FP suppression list (stage 2). Loaded on demand when the
  // user opens the management drawer or marks a new FP — we don't fetch
  // pre-emptively because the count is already in `hunt.suppressedByCustomerList`.
  const [suppressions, setSuppressions] = useState<HuntSuppression[]>([]);
  const [suppressionsLoading, setSuppressionsLoading] = useState(false);
  const [suppressionsError, setSuppressionsError] = useState<string | null>(null);
  const [manageSuppressionsOpen, setManageSuppressionsOpen] = useState(false);
  // Modal state for "Mark IOC as false positive". When set, the dialog
  // is rendered. The IOCs come from a single MatchRow's `matchedIocs[]`
  // — the analyst picks WHICH of them to suppress in the dialog.
  const [fpDraftIocs, setFpDraftIocs] = useState<string[] | null>(null);
  // Monotonic request ids used to discard stale STATE writes (don't paint
  // Customer A's data under Customer B). Separate counters for hunt vs
  // narrative because runHunt fires loadNarrative as a side-effect on
  // success — sharing one counter would cause loadNarrative's own bump
  // to invalidate runHunt's still-pending state-write guard.
  const huntReqIdRef = useRef(0);
  const narrativeReqIdRef = useRef(0);
  // Loading-flag ownership is tracked separately via in-flight COUNTS,
  // not via the staleness reqIds. Two reasons this matters:
  //  1) The customer-switch useEffect bumps the staleness reqIds (so
  //     late tenant-mismatched responses get discarded) — gating the
  //     loading reset on those bumps would leave the spinner stuck
  //     forever, which is exactly the bug we hit in the previous round.
  //  2) Gating the loading reset by "am I the LATEST request id" is
  //     wrong in the overlap case — an older request finishing AFTER
  //     a newer one started would unconditionally clear the spinner
  //     while the newer hunt is still running. With a count, loading
  //     is true iff at least one request is in flight.
  const huntInFlightRef = useRef(0);
  const narrativeInFlightRef = useRef(0);
  const [expandedMatch, setExpandedMatch] = useState<number | null>(null);
  const [reportBuilding, setReportBuilding] = useState(false);

  // AI findings narrative for the most recent hunt. Generated lazily after
  // the hunt itself returns so the user sees match data immediately and the
  // narrative streams in next.
  const [narrative, setNarrative] = useState<HuntNarrative | null>(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeError, setNarrativeError] = useState<string | null>(null);

  // ── HTML report download ─────────────────────────────────────────────────
  // Self-contained: the Kloudynet logo is inlined as a base64 data URL so
  // the file works when emailed / saved offline. The narrative may not be
  // ready yet (it streams in after the hunt) — we render whatever we have
  // and label the rest as "not generated yet".
  const downloadHuntReport = useCallback(async () => {
    if (!hunt) return;
    setReportBuilding(true);
    try {
      const logoDataUrl = await fetchAsDataUrl(kloudynetLogo);
      const html = buildHuntReportHtml({
        hunt,
        narrative,
        customerName: activeCustomer?.name ?? 'Customer',
        logoDataUrl,
      });
      const slug = (activeCustomer?.name ?? 'customer').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const stamp = new Date().toISOString().slice(0, 10);
      triggerDownload(`kloudynet-threat-hunt-${slug}-${stamp}.html`, html, 'text/html;charset=utf-8');
    } catch (e) {
      // Surface in the existing huntError banner so the user gets feedback.
      setHuntError(`Couldn't build the HTML report: ${String(e instanceof Error ? e.message : e)}`);
    } finally {
      setReportBuilding(false);
    }
  }, [hunt, narrative, activeCustomer]);

  // ── Loaders ──────────────────────────────────────────────────────────────
  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError(null);
    try {
      const resp = await fetch(`${getApiBase()}/rosti/reports?days=7`, { credentials: 'include' });
      const data = await resp.json() as RostiReportsResponse;
      if (!resp.ok) {
        setReportsError(data.error ?? `Failed (${resp.status})`);
        setReports([]);
      } else {
        setReports(data.data ?? []);
        setReportsFetchedAt(data.fetchedAt ?? new Date().toISOString());
      }
    } catch (e) {
      setReportsError(`Couldn't reach the Kloudynet TI feed: ${String(e instanceof Error ? e.message : e)}`);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const loadNarrative = useCallback(async (customerId: string, lookbackDays: number) => {
    // Same staleness guard as runHunt — prevents an old narrative from
    // landing in the panel after the user has switched customers or
    // re-run the hunt with a different window. Uses its own ref so we
    // don't invalidate an in-flight hunt's finally cleanup.
    const reqId = ++narrativeReqIdRef.current;
    narrativeInFlightRef.current += 1;
    setNarrativeLoading(true);
    setNarrativeError(null);
    setNarrative(null);
    try {
      const resp = await fetch(`${getApiBase()}/rosti/hunt/explain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customerId, lookbackDays }),
      });
      if (narrativeReqIdRef.current !== reqId) return;
      // Surface auth/expired-session and proxy/HTML responses with friendly
      // messages instead of crashing on JSON.parse. Most "Unexpected token '<'"
      // errors mean the request was intercepted before reaching the API.
      const ct = resp.headers.get('content-type') ?? '';
      if (!ct.includes('application/json')) {
        if (resp.status === 401) {
          setNarrativeError('Your session expired - please refresh the page and sign in again.');
        } else {
          setNarrativeError(`AI findings failed (${resp.status}). The request did not reach the API server.`);
        }
        return;
      }
      const data = await resp.json() as HuntNarrativeResponse;
      if (!resp.ok) {
        setNarrativeError(data.error ?? `AI findings failed (${resp.status})`);
      } else {
        setNarrative(data.narrative);
      }
    } catch (e) {
      if (narrativeReqIdRef.current !== reqId) return;
      setNarrativeError(`AI findings failed: ${String(e instanceof Error ? e.message : e)}`);
    } finally {
      // Decrement the in-flight count and only clear the spinner when
      // *all* narrative requests have settled. This survives both the
      // tenant-switch ref-bump (which doesn't touch the count) and the
      // overlap case (older request finishing while newer one is still
      // running keeps count > 0, so the spinner stays).
      narrativeInFlightRef.current = Math.max(0, narrativeInFlightRef.current - 1);
      if (narrativeInFlightRef.current === 0) setNarrativeLoading(false);
    }
  }, []);

  const runHunt = useCallback(async (force: boolean) => {
    if (!activeCustomerId) return;
    // Capture the customer + window + request id at fire time. If the user
    // switches customers (or the customer-switch effect bumps the ref)
    // before this fetch resolves, we drop the response on the floor so we
    // never paint Customer A's hits under Customer B's name.
    const reqId = ++huntReqIdRef.current;
    const reqCustomerId = activeCustomerId;
    const reqLookback = lookbackDays;
    huntInFlightRef.current += 1;
    setHuntLoading(true);
    setHuntError(null);
    setNarrative(null);
    setNarrativeError(null);
    try {
      const resp = await fetch(`${getApiBase()}/rosti/hunt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customerId: reqCustomerId, lookbackDays: reqLookback, force, bypassWarninglists }),
      });
      const data = await resp.json() as HuntResult & { error?: string };
      if (huntReqIdRef.current !== reqId) return;  // superseded — discard
      if (!resp.ok) {
        setHuntError(data.error ?? `Hunt failed (${resp.status})`);
        setHunt(null);
      } else {
        setHunt(data);
        // Auto-fire AI findings as soon as the hunt has matches. We don't
        // block on this — the hunt panel renders right away and the AI card
        // shows its own loading spinner.
        if (data.totalHits > 0) {
          void loadNarrative(reqCustomerId, data.lookbackDays);
        }
      }
    } catch (e) {
      if (huntReqIdRef.current !== reqId) return;  // superseded — discard
      setHuntError(`Hunt failed: ${String(e instanceof Error ? e.message : e)}`);
    } finally {
      // Decrement the in-flight count and only clear the spinner when
      // *all* hunt requests have settled. See huntInFlightRef declaration
      // for why we don't gate this on reqId or on the staleness check.
      huntInFlightRef.current = Math.max(0, huntInFlightRef.current - 1);
      if (huntInFlightRef.current === 0) setHuntLoading(false);
    }
  }, [activeCustomerId, loadNarrative, lookbackDays, bypassWarninglists]);

  // Load the per-customer suppression list (stage 2 FP filter). Idempotent
  // and cheap — the table is tiny per customer.
  const loadSuppressions = useCallback(async (customerId: string) => {
    setSuppressionsLoading(true);
    setSuppressionsError(null);
    try {
      const resp = await fetch(`${getApiBase()}/rosti/hunt/suppressions?customerId=${encodeURIComponent(customerId)}`, {
        credentials: 'include',
      });
      const data = await resp.json() as { suppressions?: HuntSuppression[]; error?: string };
      if (!resp.ok) {
        setSuppressionsError(data.error ?? `Failed to load suppressions (${resp.status})`);
        return;
      }
      setSuppressions(data.suppressions ?? []);
    } catch (e) {
      setSuppressionsError(`Failed to load suppressions: ${String(e instanceof Error ? e.message : e)}`);
    } finally {
      setSuppressionsLoading(false);
    }
  }, []);

  /** POST a single (customerId, iocValue, iocType) suppression. Returns
   *  true on success / already-suppressed; false on error. */
  const addSuppression = useCallback(async (
    customerId: string,
    iocValue: string,
    iocType: string,
    reason: string | null,
  ): Promise<boolean> => {
    try {
      const resp = await fetch(`${getApiBase()}/rosti/hunt/suppressions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customerId, iocValue, iocType, reason }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        setSuppressionsError(data.error ?? `Failed to add suppression (${resp.status})`);
        return false;
      }
      return true;
    } catch (e) {
      setSuppressionsError(`Failed to add suppression: ${String(e instanceof Error ? e.message : e)}`);
      return false;
    }
  }, []);

  const deleteSuppression = useCallback(async (id: string): Promise<boolean> => {
    try {
      const resp = await fetch(`${getApiBase()}/rosti/hunt/suppressions/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        setSuppressionsError(data.error ?? `Failed to remove suppression (${resp.status})`);
        return false;
      }
      return true;
    } catch (e) {
      setSuppressionsError(`Failed to remove suppression: ${String(e instanceof Error ? e.message : e)}`);
      return false;
    }
  }, []);

  useEffect(() => { void loadReports(); }, [loadReports]);
  // NOTE: hunts are explicitly user-triggered now — no auto-run on mount,
  // customer switch, or window change. The user picks a Window and clicks
  // "Run hunt" / "Re-run hunt". This avoids surprise Sentinel queries
  // (each one costs Log Analytics ingestion-billed scan time) and gives
  // the analyst full control over when to spend the budget.
  // Clear the previous customer's results when the active customer changes
  // so we don't show stale hits attributed to the wrong tenant. Also bump
  // BOTH request-id refs so any in-flight hunt/narrative fetch from the
  // previous tenant is discarded when it resolves (see runHunt /
  // loadNarrative guards).
  useEffect(() => {
    huntReqIdRef.current += 1;
    narrativeReqIdRef.current += 1;
    // Reset the in-flight counters too — any pending request from the
    // previous tenant will be discarded by the staleness guard, but its
    // finally still decrements. Resetting here keeps the count honest
    // for the new tenant's requests. Math.max in the finally protects
    // against going negative if both reset and decrement land.
    huntInFlightRef.current = 0;
    narrativeInFlightRef.current = 0;
    setHunt(null);
    setHuntError(null);
    setExpandedMatch(null);
    setNarrative(null);
    setNarrativeError(null);
    setHuntLoading(false);
    setNarrativeLoading(false);
    // Stage 2 cleanup: bypass and per-customer FP list state are
    // tenant-scoped — never carry them across customer switches or
    // we'd POST/DELETE against the wrong customer ID and (worse) leak
    // FP information from one tenant into the next tenant's UI.
    setBypassWarninglists(false);
    setSuppressions([]);
    setSuppressionsError(null);
    setManageSuppressionsOpen(false);
    setFpDraftIocs(null);
  }, [activeCustomerId]);

  return (
    <div className="slide-in space-y-5">
      <div className="rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground">
        Indicator creation has moved to the dedicated <b className="text-foreground">Manual IoC Entry</b> and <b className="text-foreground">Bulk IoC Entry</b> blades. This page now stays focused on feed-driven threat hunting and suppression management.
      </div>

      {/* ── Threat Hunt Results (last 30 days) ───────────────────────── */}
      <div className="bg-card rounded-xl shadow p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-foreground">
              Threat Hunt Results
              <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-200 px-1.5 py-0.5 rounded">
                Last {lookbackDays === 1 ? '24 hours' : `${lookbackDays} days`}
              </span>
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              We take the most recent IOCs from the Kloudynet TI feed and search the
              {activeCustomer ? ` ${activeCustomer.name}` : ''} Sentinel Log Analytics
              workspace for any hits in the last {lookbackDays === 1 ? '24 hours' : `${lookbackDays} days`}.
              {hunt?.ranAt ? ` Last run ${timeAgo(hunt.ranAt)}.` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Sentinel lookback window. Changing the value triggers a
                re-hunt via the runHunt useEffect dep — cached windows
                return instantly, fresh ones spin the loader. */}
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="hidden sm:inline">Window</span>
              <select
                value={lookbackDays}
                onChange={e => setLookbackDays(Number(e.target.value))}
                disabled={huntLoading || !canRunHunt}
                className="border border-border bg-card text-foreground px-2 py-1.5 rounded-lg text-xs focus:outline-none focus:border-primary disabled:opacity-60"
                title="Sentinel lookback window"
              >
                <option value={1}>Last 24 hours</option>
                <option value={3}>Last 3 days</option>
                <option value={7}>Last 7 days</option>
                <option value={15}>Last 15 days</option>
                <option value={30}>Last 30 days</option>
              </select>
            </label>
            {/* Download a self-contained HTML report. Only meaningful when the
                hunt actually returned hits — for a clean workspace there's
                nothing to put in a report. The HTML embeds the Kloudynet
                logo as base64 so it renders offline / when emailed. */}
            {hunt && hunt.totalHits > 0 && (
              <button
                onClick={() => void downloadHuntReport()}
                disabled={reportBuilding}
                title="Download a self-contained HTML report of the hits"
                className="border border-border bg-background hover:bg-muted text-foreground px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              >
                {reportBuilding ? 'Building…' : '⬇ Download HTML report'}
              </button>
            )}
            {/* Stage 2: analyst override for the MISP warninglist filter.
                Only visible to write-permitted users (read-only viewers
                can't change hunt parameters). Clicking it doesn't auto-
                re-hunt — the analyst still has to press Run/Re-run, and
                the new request bumps the cache key so a fresh KQL fires.
                Audit-logged on the server every time it goes out true. */}
            {canRunHunt && (
              <label
                className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none"
                title="Skip the MISP warninglist pre-filter for this hunt. Use when investigating threats on cloud/CDN-hosted infrastructure."
              >
                <input
                  type="checkbox"
                  checked={bypassWarninglists}
                  onChange={e => setBypassWarninglists(e.target.checked)}
                  disabled={huntLoading}
                  className="h-3.5 w-3.5 accent-amber-500"
                />
                <span>Bypass noise filter</span>
              </label>
            )}
            {/* Stage 2: open the per-customer FP suppression management
                drawer. Visible to anyone with read access (so customer-
                viewer roles can see what's been suppressed) but the
                drawer itself only renders Add/Remove controls when
                `canRunHunt` (write) is true. */}
            {activeCustomerId && (
              <button
                onClick={() => {
                  setManageSuppressionsOpen(true);
                  if (activeCustomerId) void loadSuppressions(activeCustomerId);
                }}
                className="border border-border bg-background hover:bg-muted text-foreground px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                title="View and manage the per-customer false-positive suppression list"
              >
                Manage suppressions
                {hunt?.suppressedByCustomerList && hunt.suppressedByCustomerList.totalSuppressed > 0 && (
                  <span className="ml-1.5 text-[10px] bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded">
                    {hunt.suppressedByCustomerList.totalSuppressed} suppressed
                  </span>
                )}
              </button>
            )}
            {canRunHunt ? (
              <button
                // First "Run hunt" honors the server cache (hunt has not
                // been run for this customer+window yet, or the prior result
                // was cleared by a tenant switch). Explicit "Re-run hunt"
                // forces a fresh Sentinel query, bypassing the cache.
                onClick={() => void runHunt(hunt !== null)}
                disabled={huntLoading || !activeCustomerId}
                className="bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {huntLoading ? 'Hunting…' : hunt ? 'Re-run hunt' : 'Run hunt'}
              </button>
            ) : (
              <span className="text-[11px] text-muted-foreground italic">
                Refreshed by your SOC team
              </span>
            )}
          </div>
        </div>

        {!activeCustomerId && (
          <div className="text-xs text-muted-foreground">Pick a customer to run the threat hunt.</div>
        )}

        {activeCustomerId && !hunt && !huntLoading && !huntError && canRunHunt && (
          <div className="text-xs text-muted-foreground border border-dashed border-border rounded p-3">
            Pick a Sentinel lookback window above and click <strong>Run hunt</strong> to
            search {activeCustomer ? activeCustomer.name : 'this customer'}'s workspace
            for the latest Kloudynet TI feed IOCs.
          </div>
        )}

        {huntError && (
          <div className="text-xs text-red-700 dark:text-red-300 mb-3">{huntError}</div>
        )}

        {hunt && (
          <>
            {/* Stat strip */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <StatCell
                label="IOCs hunted"
                value={String(hunt.iocsHunted)}
                {...(hunt.iocCoverageDays !== undefined
                  ? { sublabel: `last ${hunt.iocCoverageDays}d of feed` }
                  : {})}
              />
              <StatCell
                label="Distinct events"
                value={String(hunt.totalHits)}
                tone={hunt.totalHits > 0 ? 'warn' : 'ok'}
                {...(hunt.rawHits && hunt.rawHits > hunt.totalHits
                  ? { sublabel: `${hunt.rawHits.toLocaleString()} raw rows` }
                  : {})}
              />
              <StatCell label="Tables matched" value={String(Object.keys(hunt.hitsByTable).length)} />
              <StatCell label="Sentinel window" value={`${hunt.lookbackDays}d`} />
              <StatCell label="Query time" value={`${(hunt.durationMs / 1000).toFixed(1)}s`} />
            </div>

            {hunt.error && (
              <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded p-2 mb-3">
                The Sentinel query came back with an error: {hunt.error}
              </div>
            )}

            {/* MISP warninglist pre-filter audit. We render this whenever
                the server reported the suppression block (always, when
                warninglists loaded), even if zero — explaining 0
                suppressions is informative ("the feed was clean").
                When the analyst chose to bypass, render the bypass-state
                banner instead so they can't miss what they did. */}
            {hunt.warninglistsBypassed ? (
              <WarninglistBypassedBanner />
            ) : hunt.suppressedByWarninglist ? (
              <WarninglistSuppressionBanner summary={hunt.suppressedByWarninglist} />
            ) : null}

            {/* Per-customer FP list audit. Only render when there's
                something to show — a brand-new tenant with an empty
                suppression list shouldn't see a stub "0 suppressed"
                chip every time. */}
            {hunt.suppressedByCustomerList && hunt.suppressedByCustomerList.totalSuppressed > 0 && (
              <CustomerSuppressionBanner
                summary={hunt.suppressedByCustomerList}
                onManage={() => {
                  setManageSuppressionsOpen(true);
                  if (activeCustomerId) void loadSuppressions(activeCustomerId);
                }}
              />
            )}

            {/* AI Findings — only when there are matches to summarise */}
            {hunt.totalHits > 0 && (
              <AIFindingsCard
                narrative={narrative}
                loading={narrativeLoading}
                error={narrativeError}
                onRegenerate={() => {
                  if (activeCustomerId) void loadNarrative(activeCustomerId, hunt.lookbackDays);
                }}
              />
            )}

            {/* Top reports that matched */}
            {hunt.topReports.length > 0 && (
              <div className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  Threat reports linked to the hits
                </div>
                <div className="space-y-1">
                  {hunt.topReports.map(r => (
                    <a
                      key={r.id}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-primary hover:underline"
                    >
                      {r.title} <span className="text-muted-foreground">({r.iocCount} IOC{r.iocCount === 1 ? '' : 's'} matched)</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Top IOCs that hit */}
            {Object.keys(hunt.hitsByIoc).length > 0 && (
              <div className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  IOCs that hit
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(hunt.hitsByIoc)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 20)
                    .map(([ioc, count]) => (
                      <span
                        key={ioc}
                        className="font-mono text-[11px] bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 px-2 py-0.5 rounded"
                        title={`${count} hit${count === 1 ? '' : 's'}`}
                      >
                        {ioc} <span className="opacity-60">×{count}</span>
                      </span>
                    ))}
                </div>
              </div>
            )}

            {/* Match feed */}
            {hunt.matches.length === 0 && !hunt.error && !huntLoading && (
              <div className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded p-3">
                Nothing to flag. None of the {hunt.iocsHunted} hunted IOCs turned up in the last
                {' '}{hunt.lookbackDays === 1 ? '24 hours' : `${hunt.lookbackDays} days`} of {hunt.workspaceName ?? 'the customer'} workspace logs.
              </div>
            )}

            {hunt.matches.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Matches ({hunt.matches.length}{hunt.truncated ? '+ truncated' : ''})
                  </div>
                </div>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/80 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground whitespace-nowrap">Time</th>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Source table</th>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Matched IOC</th>
                        <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hunt.matches.map((m, i) => (
                        <MatchRow
                          key={i}
                          m={m}
                          expanded={expandedMatch === i}
                          onToggle={() => setExpandedMatch(expandedMatch === i ? null : i)}
                          {...(canRunHunt
                            ? { onMarkFp: (iocs: string[]) => setFpDraftIocs(iocs) }
                            : {})}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {!hunt && huntLoading && (
          <div className="text-xs text-muted-foreground">Running the hunt against Sentinel - this can take 20-60 seconds…</div>
        )}

        {!hunt && !huntLoading && !huntError && activeCustomerId && !canRunHunt && (
          <div className="text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg px-3 py-2.5">
            Threat hunt results haven't been refreshed in this session yet. Your SOC
            team runs the hunt on a regular cadence - the latest findings will appear
            here once they do.
          </div>
        )}
      </div>

      {/* ── Latest Threat Intel Reports (Rösti) ─────────────────────── */}
      <div className="bg-card rounded-xl shadow p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-foreground">Latest Threat Intel Reports - Last 7 days</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Reports published in the last 7 days, pulled from the Threat Intelligence feed.
              {reportsFetchedAt ? ` Refreshed ${timeAgo(reportsFetchedAt)}.` : ''}
            </p>
          </div>
          <button
            onClick={() => void loadReports()}
            disabled={reportsLoading}
            className="border border-border bg-card px-3 py-1.5 rounded-lg text-xs text-foreground hover:bg-muted disabled:opacity-60 transition-colors"
          >
            {reportsLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {reportsError && (
          <div className="text-xs text-red-700 dark:text-red-300 mb-2">{reportsError}</div>
        )}

        {reports.length === 0 && !reportsLoading && !reportsError && (
          <div className="text-xs text-muted-foreground">No reports available right now.</div>
        )}

        {reports.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/80">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Source</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Title</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground">IOCs</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground">CVEs</th>
                  <th className="px-3 py-2 text-right font-semibold text-muted-foreground">MITRE</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Tags</th>
                </tr>
              </thead>
              <tbody>
                {reports.map(r => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.date}</td>
                    <td className="px-3 py-2 text-foreground">{r.source?.name ?? r.source?.id ?? ' - '}</td>
                    <td className="px-3 py-2 max-w-[420px]">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline font-medium line-clamp-2"
                        title={r.title}
                      >
                        {r.title}
                      </a>
                      {r.authors && r.authors.length > 0 && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                          {r.authors.slice(0, 3).join(', ')}{r.authors.length > 3 ? '…' : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-foreground">{r.count?.iocs ?? 0}</td>
                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">{r.count?.cve ?? 0}</td>
                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">{r.count?.mitre_ids ?? 0}</td>
                    <td className="px-3 py-2 max-w-[200px]">
                      <div className="flex flex-wrap gap-1">
                        {(r.tags ?? []).slice(0, 3).map(t => (
                          <span key={t} className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-[10px]">
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stage 2: Mark IOC as FP modal. Only visible when an analyst
          clicked "Mark FP" on a row AND we have an active customer
          (the latter should always be true alongside the former since
          the FP button only renders for `canRunHunt`). */}
      {fpDraftIocs && activeCustomerId && (
        <MarkFpDialog
          customerId={activeCustomerId}
          iocs={fpDraftIocs}
          onClose={() => setFpDraftIocs(null)}
          onSubmit={async (selected, reason) => {
            // Bulk add: walk the selected list serially so a partial
            // failure doesn't leave us with no audit trail. The server
            // is idempotent (onConflictDoNothing) so retries are safe.
            let anySucceeded = false;
            for (const item of selected) {
              const ok = await addSuppression(activeCustomerId, item.value, item.type, reason);
              if (ok) anySucceeded = true;
            }
            setFpDraftIocs(null);
            if (anySucceeded) {
              // Refresh the management drawer's list (in case it's open
              // or about to be opened) and re-run the hunt so the newly
              // suppressed IOCs disappear from the matches table.
              void loadSuppressions(activeCustomerId);
              void runHunt(true);
            }
          }}
        />
      )}

      {/* Stage 2: Manage suppressions drawer. Always mounted (so we can
          do an entrance animation later) but hidden via prop. */}
      {manageSuppressionsOpen && activeCustomerId && (
        <ManageSuppressionsDialog
          customerId={activeCustomerId}
          suppressions={suppressions}
          loading={suppressionsLoading}
          error={suppressionsError}
          canWrite={canRunHunt}
          onClose={() => setManageSuppressionsOpen(false)}
          onRefresh={() => void loadSuppressions(activeCustomerId)}
          onDelete={async (id) => {
            const ok = await deleteSuppression(id);
            if (ok) {
              void loadSuppressions(activeCustomerId);
              // Removing a suppression means future hunts should re-find
              // the IOC. Force-refresh so the UI matches reality.
              void runHunt(true);
            }
          }}
        />
      )}
    </div>
  );
}

/**
 * Banner showing how many Rosti IOCs were filtered out before hunting via
 * the bundled MISP warninglists. Click "Show details" to expand a drawer
 * listing per-list counts and a sample of the suppressed values — this is
 * the audit trail an analyst needs when they ask "did the hunt skip
 * something I cared about?".
 *
 * Rendered whenever the server reports the suppression block (which is
 * always, when warninglists loaded successfully). Zero suppressions still
 * render — that's a meaningful signal ("the feed was clean today").
 */
function WarninglistSuppressionBanner({ summary }: { summary: SuppressionSummary }): ReactElement {
  const [open, setOpen] = useState(false);
  const total = summary.totalSuppressed;
  const listCount = summary.byList.length;

  if (total === 0) {
    return (
      <div className="text-[11px] text-muted-foreground bg-muted/40 border border-border rounded px-2.5 py-1.5 mb-3 flex items-center gap-2">
        <span className="font-semibold">Noise filter:</span>
        <span>No Rosti IOCs matched any of the bundled MISP warninglists for this batch.</span>
      </div>
    );
  }

  return (
    <div className="text-xs bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800 rounded mb-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-2 flex items-center justify-between gap-3 hover:bg-sky-100/60 dark:hover:bg-sky-900/30 rounded"
      >
        <div className="flex items-center gap-2 text-sky-900 dark:text-sky-100">
          <span aria-hidden>🛡️</span>
          <span>
            <span className="font-semibold">{total.toLocaleString()}</span> known-benign IOC
            {total === 1 ? '' : 's'} filtered out before hunting
            <span className="text-sky-700 dark:text-sky-300"> · matched {listCount} MISP warninglist{listCount === 1 ? '' : 's'}</span>
          </span>
        </div>
        <span className="text-sky-700 dark:text-sky-300 text-[11px] underline-offset-2 hover:underline">
          {open ? 'Hide details' : 'Show details'}
        </span>
      </button>

      {open && (
        <div className="border-t border-sky-200 dark:border-sky-800 px-3 py-2.5 space-y-3">
          <div className="text-[11px] text-sky-900/80 dark:text-sky-100/80 leading-snug">
            These IOCs were never sent to Sentinel because they matched a curated{' '}
            <a
              href="https://github.com/MISP/misp-warninglists"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              MISP warninglist
            </a>
            {' '}of known-benign indicators (RFC1918 ranges, hyperscaler CIDRs, public DNS resolvers,
            popular hostnames, raw TLDs, etc.). Filtering at IOC selection - rather than after the
            KQL - keeps queries cheap and avoids drowning the match feed in CDN noise.
          </div>

          {/* Per-list counts */}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-900 dark:text-sky-100 mb-1">
              By warninglist
            </div>
            <div className="space-y-0.5">
              {summary.byList.map((b) => (
                <div key={b.list} className="flex items-baseline justify-between gap-3 text-[11px]">
                  <span className="text-sky-900 dark:text-sky-100 truncate" title={b.listName}>
                    {b.listName}
                  </span>
                  <span className="font-mono text-sky-700 dark:text-sky-300 tabular-nums">
                    {b.count.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Sample of suppressed IOCs (capped server-side) */}
          {summary.sample.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-900 dark:text-sky-100 mb-1">
                Sample of suppressed IOCs ({summary.sample.length} of {total.toLocaleString()})
              </div>
              <div className="flex flex-wrap gap-1">
                {summary.sample.map((s, i) => (
                  <span
                    key={`${s.list}-${s.value}-${i}`}
                    className="font-mono text-[10px] bg-white/70 dark:bg-sky-950/50 text-sky-900 dark:text-sky-100 border border-sky-200 dark:border-sky-800 px-1.5 py-0.5 rounded"
                    title={`Suppressed by: ${s.listName}`}
                  >
                    {s.value}
                    <span className="text-sky-600 dark:text-sky-400 ml-1 opacity-70">
                      [{s.type}]
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Stage 2: Banner shown when the analyst chose to bypass the MISP
 * warninglist filter. Loud amber treatment because this materially
 * changes the meaning of the result set — the analyst should never miss
 * that they're seeing the unfiltered hunt.
 */
function WarninglistBypassedBanner(): ReactElement {
  return (
    <div className="border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-lg px-3 py-2 mb-3 text-xs text-amber-900 dark:text-amber-100">
      <div className="flex items-center gap-2">
        <span className="font-bold uppercase tracking-wide text-[10px] bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 px-1.5 py-0.5 rounded">
          Filter bypassed
        </span>
        <span>
          MISP warninglist pre-filter was disabled for this hunt. Cloud / CDN /
          public DNS IOCs were <strong>not</strong> suppressed - expect more
          benign-looking matches that need analyst judgment.
        </span>
      </div>
    </div>
  );
}

/**
 * Stage 2: Banner showing how many IOCs were dropped by the per-customer
 * analyst-curated FP list. Clicking "Manage" opens the management drawer
 * so the analyst can audit / un-suppress.
 */
function CustomerSuppressionBanner({
  summary,
  onManage,
}: {
  summary: CustomerSuppressionSummary;
  onManage: () => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg px-3 py-2 mb-3 text-xs text-emerald-900 dark:text-emerald-100">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="font-bold uppercase tracking-wide text-[10px] bg-emerald-200 dark:bg-emerald-800 text-emerald-900 dark:text-emerald-100 px-1.5 py-0.5 rounded">
            Customer FP list
          </span>
          <span>
            <strong>{summary.totalSuppressed}</strong> IOC{summary.totalSuppressed === 1 ? '' : 's'} skipped
            because your team marked them as known false positives.
          </span>
        </div>
        <div className="flex items-center gap-2">
          {summary.sample.length > 0 && (
            <button
              onClick={() => setOpen(o => !o)}
              className="text-[11px] underline hover:no-underline opacity-80 hover:opacity-100"
            >
              {open ? 'Hide details' : 'Show details'}
            </button>
          )}
          <button
            onClick={onManage}
            className="border border-emerald-300 dark:border-emerald-700 bg-white/40 dark:bg-emerald-900/40 hover:bg-white/70 dark:hover:bg-emerald-900/70 text-emerald-900 dark:text-emerald-100 px-2 py-0.5 rounded text-[11px] font-semibold"
          >
            Manage
          </button>
        </div>
      </div>
      {open && summary.sample.length > 0 && (
        <div className="mt-2 pt-2 border-t border-emerald-200 dark:border-emerald-800/60">
          <div className="flex flex-wrap gap-1.5">
            {summary.sample.slice(0, 50).map((s, i) => (
              <span
                key={i}
                className="font-mono text-[10px] bg-white/60 dark:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 px-1.5 py-0.5 rounded"
                title={s.reason ?? `${s.type} suppressed for this customer`}
              >
                <span className="opacity-60">{s.type}:</span> {s.value}
              </span>
            ))}
            {summary.sample.length > 50 && (
              <span className="text-[10px] opacity-70 self-center">
                +{summary.sample.length - 50} more - open Manage to see all
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Stage 2: Dialog used to mark one or more IOCs as a false positive for
 * the active customer. Multi-select because a single MatchRow may carry
 * several matched IOCs and the analyst usually wants to suppress only
 * the noisy one(s) — not all of them.
 *
 * The IOC type for each row isn't carried by HuntMatch.matchedIocs (it's
 * just strings). We make a best-effort guess from the value's shape:
 * IPv4/IPv6 → "ip", looks-like-domain → "domain", 32/40/64 hex → "hash",
 * else "url". Server validates the chosen type via Zod.
 */
function MarkFpDialog({
  customerId: _customerId,
  iocs,
  onClose,
  onSubmit,
}: {
  customerId: string;
  iocs: string[];
  onClose: () => void;
  onSubmit: (selected: { value: string; type: string }[], reason: string | null) => Promise<void>;
}): ReactElement {
  const guessType = (v: string): string => {
    const s = v.trim();
    if (/^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/.test(s)) return 'ip';
    if (/^[a-f0-9:]+(\/\d{1,3})?$/i.test(s) && s.includes(':')) return 'ip';
    if (/^[a-f0-9]{32}$/i.test(s) || /^[a-f0-9]{40}$/i.test(s) || /^[a-f0-9]{64}$/i.test(s)) return 'hash';
    if (/^https?:\/\//i.test(s)) return 'url';
    if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(s)) return 'domain';
    return 'url';
  };
  const initialChoices = iocs.map(v => ({ value: v, type: guessType(v), checked: true }));
  const [choices, setChoices] = useState(initialChoices);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const selectedCount = choices.filter(c => c.checked).length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">Mark IOC{iocs.length > 1 ? 's' : ''} as false positive</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Future hunts for this customer will skip the selected IOCs before
            querying Sentinel. You can un-suppress at any time from
            <strong> Manage suppressions</strong>.
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div className="space-y-2">
            {choices.map((c, idx) => (
              <label key={idx} className="flex items-start gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={c.checked}
                  onChange={e => setChoices(prev => prev.map((p, i) => i === idx ? { ...p, checked: e.target.checked } : p))}
                  className="mt-0.5 h-3.5 w-3.5"
                />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-foreground break-all">{c.value}</div>
                  <div className="mt-0.5">
                    <select
                      value={c.type}
                      onChange={e => setChoices(prev => prev.map((p, i) => i === idx ? { ...p, type: e.target.value } : p))}
                      className="border border-border bg-background text-foreground px-1.5 py-0.5 rounded text-[10px] focus:outline-none focus:border-primary"
                    >
                      <option value="ip">ip</option>
                      <option value="domain">domain</option>
                      <option value="url">url</option>
                      <option value="hash">hash</option>
                    </select>
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-muted-foreground mb-1">
              Reason (optional, max 500 chars)
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value.slice(0, 500))}
              placeholder="e.g. internal egress proxy, known business partner CDN..."
              rows={2}
              className="w-full border border-border bg-background text-foreground px-2 py-1.5 rounded text-xs focus:outline-none focus:border-primary"
            />
            <div className="text-[10px] text-muted-foreground mt-0.5 text-right">{reason.length}/500</div>
          </div>
        </div>
        <div className="p-3 border-t border-border flex items-center justify-end gap-2 bg-muted/30">
          <button
            onClick={onClose}
            disabled={submitting}
            className="border border-border bg-background hover:bg-muted text-foreground px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              setSubmitting(true);
              try {
                await onSubmit(
                  choices.filter(c => c.checked).map(c => ({ value: c.value, type: c.type })),
                  reason.trim() ? reason.trim() : null,
                );
              } finally {
                setSubmitting(false);
              }
            }}
            disabled={submitting || selectedCount === 0}
            className="bg-primary text-primary-foreground px-3 py-1.5 rounded text-xs font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : `Suppress ${selectedCount} IOC${selectedCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Stage 2: Drawer to view and remove the per-customer FP suppression
 * list. Read-only viewers (no write perm) see the list but no Remove
 * button — useful for transparency without giving them mutation rights.
 */
function ManageSuppressionsDialog({
  customerId: _customerId,
  suppressions,
  loading,
  error,
  canWrite,
  onClose,
  onRefresh,
  onDelete,
}: {
  customerId: string;
  suppressions: HuntSuppression[];
  loading: boolean;
  error: string | null;
  canWrite: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onDelete: (id: string) => Promise<void>;
}): ReactElement {
  const [filter, setFilter] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const filtered = suppressions.filter(s => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return s.iocValue.toLowerCase().includes(q)
      || s.iocType.toLowerCase().includes(q)
      || (s.reason ?? '').toLowerCase().includes(q);
  });
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-foreground">Customer FP suppression list</h3>
            <p className="text-xs text-muted-foreground mt-1">
              IOCs in this list are skipped before any Sentinel query is sent
              for this customer. Hunts will re-find them as soon as the entry
              is removed.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-3 border-b border-border flex items-center gap-2">
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter by value, type, or reason…"
            className="flex-1 border border-border bg-background text-foreground px-2 py-1.5 rounded text-xs focus:outline-none focus:border-primary"
          />
          <button
            onClick={onRefresh}
            disabled={loading}
            className="border border-border bg-background hover:bg-muted text-foreground px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {error && (
            <div className="text-xs text-red-700 dark:text-red-300 mb-2">{error}</div>
          )}
          {!loading && suppressions.length === 0 && !error && (
            <div className="text-xs text-muted-foreground italic text-center py-6">
              No suppressions yet. Mark a hunt match as FP to add the first one.
            </div>
          )}
          {filtered.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Type</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Value</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Reason</th>
                  <th className="px-2 py-1.5 text-left font-semibold text-muted-foreground">Added</th>
                  {canWrite && <th className="px-2 py-1.5"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(s => (
                  <tr key={s.id} className="border-b border-border/60 hover:bg-muted/30">
                    <td className="px-2 py-1.5 align-top">
                      <span className="bg-muted text-foreground px-1.5 py-0.5 rounded text-[10px] font-mono">
                        {s.iocType}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 align-top font-mono break-all text-foreground">{s.iocValue}</td>
                    <td className="px-2 py-1.5 align-top text-muted-foreground">{s.reason ?? <span className="opacity-50 italic"> - </span>}</td>
                    <td className="px-2 py-1.5 align-top text-muted-foreground whitespace-nowrap">
                      {new Date(s.createdAt).toISOString().slice(0, 10)}
                      {s.suppressedBy && (
                        <div className="text-[10px] opacity-70">by {s.suppressedBy}</div>
                      )}
                    </td>
                    {canWrite && (
                      <td className="px-2 py-1.5 align-top text-right">
                        <button
                          onClick={async () => {
                            setBusyId(s.id);
                            try {
                              await onDelete(s.id);
                            } finally {
                              setBusyId(null);
                            }
                          }}
                          disabled={busyId === s.id}
                          className="border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-700 dark:text-red-200 px-2 py-0.5 rounded text-[10px] font-semibold disabled:opacity-50"
                        >
                          {busyId === s.id ? '…' : 'Remove'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && suppressions.length > 0 && filtered.length === 0 && (
            <div className="text-xs text-muted-foreground italic text-center py-4">
              No suppressions match "{filter}".
            </div>
          )}
        </div>
        <div className="p-3 border-t border-border flex items-center justify-between bg-muted/30">
          <span className="text-[11px] text-muted-foreground">
            {suppressions.length} suppression{suppressions.length === 1 ? '' : 's'} total
          </span>
          <button
            onClick={onClose}
            className="border border-border bg-background hover:bg-muted text-foreground px-3 py-1.5 rounded text-xs font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function AIFindingsCard({
  narrative,
  loading,
  error,
  onRegenerate,
}: {
  narrative: HuntNarrative | null;
  loading: boolean;
  error: string | null;
  onRegenerate: () => void;
}): ReactElement {
  // Verdict colour mapping. CLEAN should never appear (the route only
  // generates a narrative when totalHits > 0) but we handle it defensively.
  const verdictTone =
    narrative?.verdict === 'ACTIVE_THREAT' ? {
      bg: 'bg-red-50 dark:bg-red-950/30',
      border: 'border-red-200 dark:border-red-800',
      pill: 'bg-red-600 text-white',
      label: 'ACTIVE THREAT',
    } : narrative?.verdict === 'INVESTIGATE' ? {
      bg: 'bg-amber-50 dark:bg-amber-950/30',
      border: 'border-amber-200 dark:border-amber-800',
      pill: 'bg-amber-500 text-white',
      label: 'INVESTIGATE',
    } : {
      bg: 'bg-emerald-50 dark:bg-emerald-950/30',
      border: 'border-emerald-200 dark:border-emerald-800',
      pill: 'bg-emerald-600 text-white',
      label: 'CLEAN',
    };

  return (
    <div className={`mb-4 rounded-lg border ${narrative ? verdictTone.border : 'border-violet-200 dark:border-violet-800'} ${narrative ? verdictTone.bg : 'bg-violet-50/50 dark:bg-violet-950/20'} p-4`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wide bg-violet-600 text-white px-1.5 py-0.5 rounded">
            AI Findings
          </span>
          {narrative && (
            <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${verdictTone.pill}`}>
              {verdictTone.label}
            </span>
          )}
        </div>
        <button
          onClick={onRegenerate}
          disabled={loading}
          className="text-[11px] text-violet-700 dark:text-violet-300 hover:underline disabled:opacity-50"
        >
          {loading ? 'Generating…' : 'Regenerate'}
        </button>
      </div>

      {loading && !narrative && (
        <div className="text-xs text-muted-foreground italic">
          Analysing the hunt results with AI - this usually takes 10-30 seconds…
        </div>
      )}

      {error && !loading && (
        <div className="text-xs text-amber-700 dark:text-amber-300">
          Could not generate AI findings: {error}
          <div className="mt-1 text-[11px] text-muted-foreground">
            The raw hunt results below are still accurate; the AI summary is just a convenience layer.
          </div>
        </div>
      )}

      {narrative && (
        <>
          {narrative.headline && (
            <div className="font-semibold text-foreground mb-2 text-sm">{narrative.headline}</div>
          )}
          {narrative.summary && (
            <div className="text-xs text-foreground whitespace-pre-line leading-relaxed mb-3">
              {narrative.summary}
            </div>
          )}

          {narrative.keyFindings.length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Key findings
              </div>
              <ul className="text-xs text-foreground space-y-1 list-disc pl-5">
                {narrative.keyFindings.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}

          {(narrative.affectedDevices.length > 0 || narrative.affectedUsers.length > 0) && (
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              {narrative.affectedDevices.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Devices affected
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {narrative.affectedDevices.map((d, i) => (
                      <span key={i} className="font-mono text-[11px] bg-card border border-border text-foreground px-1.5 py-0.5 rounded">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {narrative.affectedUsers.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    Users affected
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {narrative.affectedUsers.map((u, i) => (
                      <span key={i} className="font-mono text-[11px] bg-card border border-border text-foreground px-1.5 py-0.5 rounded">
                        {u}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {narrative.rootCauseAnalysis.length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                Root cause - how each IOC entered the environment
              </div>
              <div className="space-y-2">
                {narrative.rootCauseAnalysis.map((rca, i) => (
                  <RcaRow key={`${rca.ioc}-${i}`} rca={rca} />
                ))}
              </div>
            </div>
          )}

          {narrative.recommendations.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Recommended next steps
              </div>
              <ol className="text-xs text-foreground space-y-1 list-decimal pl-5">
                {narrative.recommendations.map((r, i) => <li key={i}>{r}</li>)}
              </ol>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Each action kind gets a short label + a single emoji-free monogram
// (analysts dislike emoji clutter in incident reports). Colours are grouped:
// red = on-host activity (the worst), amber = identity / perimeter,
// violet = mail-borne, slate = passive observation.
const ACTION_DISPLAY: Record<IocActionKind, { label: string; tone: string }> = {
  url_clicked:      { label: 'URL clicked',       tone: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 border-red-300 dark:border-red-800' },
  file_written:     { label: 'File written',      tone: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 border-red-300 dark:border-red-800' },
  process_executed: { label: 'Process executed',  tone: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 border-red-300 dark:border-red-800' },
  image_loaded:     { label: 'Image loaded',      tone: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 border-red-300 dark:border-red-800' },
  network_call:     { label: 'Network call',      tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300 dark:border-amber-800' },
  device_event:     { label: 'Defender event',    tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300 dark:border-amber-800' },
  sign_in:          { label: 'Sign-in',           tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300 dark:border-amber-800' },
  saas_activity:    { label: 'SaaS activity',     tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300 dark:border-amber-800' },
  windows_event:    { label: 'Windows event',     tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300 dark:border-amber-800' },
  email_received:   { label: 'Email delivered',   tone: 'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300 border-violet-300 dark:border-violet-800' },
  url_in_email:     { label: 'URL in email',      tone: 'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300 border-violet-300 dark:border-violet-800' },
  perimeter_seen:   { label: 'Perimeter hit',     tone: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300 border-slate-300 dark:border-slate-700' },
  dns_query:        { label: 'DNS query',         tone: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300 border-slate-300 dark:border-slate-700' },
  other:            { label: 'Hit',               tone: 'bg-muted text-muted-foreground border-border' },
};

function formatTs(ts: string): string {
  if (!ts) return ' - ';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  // Compact UTC: "Apr 22, 14:32 UTC"
  return d.toLocaleString('en-GB', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }) + ' UTC';
}

function formatDuration(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return 'single event';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
}

function RcaRow({ rca }: { rca: IocRootCause }): ReactElement {
  // Vector → colour. C2 / Lateral Movement / Valid Accounts (post-compromise
  // tactics) are the most alarming so they go red; perimeter/exposure
  // vectors are amber; phishing/drive-by/supply-chain are violet.
  const vectorTone =
    rca.vector === 'Command and Control' || rca.vector === 'Lateral Movement' || rca.vector === 'Valid Accounts'
      ? 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 border-red-300 dark:border-red-800'
      : rca.vector === 'Exploit Public-Facing Application' || rca.vector === 'External Remote Services'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border-amber-300 dark:border-amber-800'
      : rca.vector === 'Unknown'
      ? 'bg-muted text-muted-foreground border-border'
      : 'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300 border-violet-300 dark:border-violet-800';

  const confTone =
    rca.confidence === 'High' ? 'bg-foreground/80 text-background'
    : rca.confidence === 'Medium' ? 'bg-foreground/40 text-background'
    : 'bg-foreground/20 text-foreground';

  return (
    <div className="rounded border border-border bg-background/50 p-2.5">
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
        <span className="font-mono text-[11px] text-foreground bg-card border border-border px-1.5 py-0.5 rounded break-all">
          {rca.ioc}
        </span>
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${vectorTone}`}>
          {rca.vector}
        </span>
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${confTone}`}>
          {rca.confidence} confidence
        </span>
      </div>

      {/* Activity timeline strip — start → end → duration → distinct days.
          Always rendered (server-computed, never empty for a hit IOC). */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground mb-1.5 border-y border-border/60 py-1">
        <span><span className="text-muted-foreground/70">First </span><span className="text-foreground font-mono">{formatTs(rca.timeline.firstSeen)}</span></span>
        <span className="text-muted-foreground/40">→</span>
        <span><span className="text-muted-foreground/70">Last </span><span className="text-foreground font-mono">{formatTs(rca.timeline.lastSeen)}</span></span>
        <span className="text-muted-foreground/40">·</span>
        <span><span className="text-muted-foreground/70">Span </span><span className="text-foreground">{formatDuration(rca.timeline.durationHours)}</span></span>
        <span className="text-muted-foreground/40">·</span>
        <span><span className="text-muted-foreground/70">Active days </span><span className="text-foreground">{rca.timeline.activeDays}</span></span>
        {rca.timeline.peakDay && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span><span className="text-muted-foreground/70">Peak </span><span className="text-foreground font-mono">{rca.timeline.peakDay.date}</span> ({rca.timeline.peakDay.hits})</span>
          </>
        )}
      </div>

      {rca.narrative && (
        <div className="text-xs text-foreground leading-relaxed mb-1.5">
          {rca.narrative}
        </div>
      )}

      {/* User/device actions — the headline of this card for a SOC analyst.
          Rendered server-side from the matched rows, so always shows real
          clicks/downloads/executions even if the AI narrative is sparse. */}
      {rca.actions.length > 0 && (
        <div className="mb-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
            What happened ({rca.actions.length} {rca.actions.length === 1 ? 'event' : 'events'})
          </div>
          <ul className="space-y-1">
            {rca.actions.map((a, i) => {
              const disp = ACTION_DISPLAY[a.kind];
              return (
                <li key={`${a.timestamp}-${i}`} className="flex items-start gap-1.5">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0 ${disp.tone}`}>
                    {disp.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] text-foreground leading-snug break-words">{a.summary}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {formatTs(a.timestamp)} · {a.table}
                      {a.user ? ` · user ${a.user}` : ''}
                      {a.device ? ` · host ${a.device}` : ''}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {rca.tablesPresent.length > 0 && (
        <div className="text-[10px] text-muted-foreground mb-1">
          Seen in:{' '}
          {rca.tablesPresent.map((t, i) => (
            <span key={t} className="font-mono text-foreground">
              {t}{i < rca.tablesPresent.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      )}

      {rca.evidence.length > 0 && (
        <ul className="text-[11px] text-muted-foreground space-y-0.5 list-disc pl-4">
          {rca.evidence.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
    </div>
  );
}

function StatCell({ label, value, tone, sublabel }: { label: string; value: string; tone?: 'ok' | 'warn'; sublabel?: string }): ReactElement {
  const valueClass =
    tone === 'warn' ? 'text-red-600 dark:text-red-400' :
    tone === 'ok'   ? 'text-emerald-600 dark:text-emerald-400' :
                      'text-foreground';
  return (
    <div className="border border-border rounded-lg p-3 bg-muted/20">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${valueClass}`}>{value}</div>
      {sublabel && (
        <div className="text-[10px] text-muted-foreground mt-0.5">{sublabel}</div>
      )}
    </div>
  );
}

function MatchRow({ m, expanded, onToggle, onMarkFp }: {
  m: HuntMatch;
  expanded: boolean;
  onToggle: () => void;
  /** Stage 2: when provided, render an inline "FP" button that hands the
   *  row's matched IOCs back to the parent so it can open the suppression
   *  dialog. Omitted for read-only viewers (no write perm on threat_intel). */
  onMarkFp?: (iocs: string[]) => void;
}): ReactElement {
  const occurrences = m.occurrences ?? 1;
  const firstSeen = m.firstSeen ?? m.timestamp;
  const lastSeen = m.lastSeen ?? m.timestamp;
  const fmt = (ts: string) => ts ? new Date(ts).toISOString().replace('T', ' ').slice(0, 19) : ' - ';
  const time = fmt(firstSeen);
  const range = occurrences > 1 && lastSeen && lastSeen !== firstSeen
    ? `→ ${fmt(lastSeen)}`
    : '';
  // Pull a couple of "interesting" fields out of the raw row so the analyst
  // doesn't have to expand every match to know what they're looking at.
  const interesting: { k: string; v: string }[] = [];
  const candidates = [
    'Computer', 'DeviceName', 'SourceIP', 'DestinationIP', 'SrcIP', 'DstIP',
    'RequestURL', 'Url', 'FileName', 'InitiatingProcessFileName',
    'AccountName', 'UserPrincipalName', 'SenderFromAddress',
  ];
  for (const k of candidates) {
    const v = m.raw?.[k];
    if (typeof v === 'string' && v.length > 0 && v.length < 200) {
      interesting.push({ k, v });
      if (interesting.length >= 3) break;
    }
  }
  return (
    <>
      <tr className="border-t border-border hover:bg-muted/30 transition-colors cursor-pointer" onClick={onToggle}>
        <td className="px-3 py-2 font-mono text-muted-foreground whitespace-nowrap">
          <div>{time}</div>
          {range && <div className="text-[10px] opacity-70">{range}</div>}
        </td>
        <td className="px-3 py-2">
          <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-[10px] font-semibold">
            {m.table}
          </span>
          {occurrences > 1 && (
            <span
              className="ml-1.5 bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded text-[10px] font-semibold"
              title={`${occurrences} raw rows collapsed into this distinct event`}
            >
              ×{occurrences}
            </span>
          )}
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap gap-1">
            {m.matchedIocs.slice(0, 3).map(ioc => (
              <span key={ioc} className="font-mono text-[11px] bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded">
                {ioc}
              </span>
            ))}
            {m.matchedIocs.length > 3 && <span className="text-[10px] text-muted-foreground">+{m.matchedIocs.length - 3}</span>}
          </div>
        </td>
        <td className="px-3 py-2 text-muted-foreground">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {interesting.length > 0
                ? interesting.map(({ k, v }) => `${k}=${v}`).join(' · ')
                : <span className="opacity-60">click to view raw row</span>}
            </div>
            {onMarkFp && (
              <button
                onClick={(e) => { e.stopPropagation(); onMarkFp(m.matchedIocs); }}
                className="shrink-0 border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-900/60 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors"
                title="Mark one of this row's matched IOCs as a known false positive - future hunts for this customer will skip it before querying Sentinel."
              >
                Mark FP
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-border bg-muted/40">
          <td colSpan={4} className="px-3 py-2">
            <pre className="text-[10px] font-mono bg-background border border-border rounded p-2 overflow-x-auto max-h-64 text-foreground">
{JSON.stringify(m.raw, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

// ── HTML report helpers ────────────────────────────────────────────────────
// Kept module-level (not inside the component) so they're tree-shakeable
// and don't re-allocate on every render. The HTML produced is fully
// self-contained: inline CSS, no external scripts, logo embedded as a
// base64 data URL — safe to email or archive.

/**
 * Allow only http(s) URLs in the exported HTML. A malicious threat-report
 * record could otherwise carry a `javascript:` URL that would execute when
 * the recipient clicks the link in the downloaded report. Anything that
 * isn't plainly http(s) is rewritten to `#`.
 */
function safeHref(url: unknown): string {
  if (typeof url !== 'string') return '#';
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) return '#';
  return trimmed;
}

/** HTML-escape a value to prevent stored IOCs/log fields from breaking the report. */
function esc(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Fetch a bundled asset (e.g. the Kloudynet logo) and return it as a
 * `data:` URL so it can be embedded into a downloaded HTML file. Vite
 * gives us the asset URL at build time; the fetch is same-origin and
 * cached, so the cost is negligible.
 */
async function fetchAsDataUrl(url: string): Promise<string> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`logo fetch failed (${resp.status})`);
  const blob = await resp.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('logo read failed'));
    reader.readAsDataURL(blob);
  });
}

/** Trigger a browser download for an in-memory string. */
function triggerDownload(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function buildHuntReportHtml(args: {
  hunt: HuntResult;
  narrative: HuntNarrative | null;
  customerName: string;
  logoDataUrl: string;
}): string {
  const { hunt, narrative, customerName, logoDataUrl } = args;
  const generated = new Date().toLocaleString();
  const ranAt = hunt.ranAt ? fmtTs(hunt.ranAt) : 'unknown';

  const verdictBadge = (() => {
    if (!narrative) return '';
    const tone =
      narrative.verdict === 'ACTIVE_THREAT' ? '#b91c1c' :
      narrative.verdict === 'INVESTIGATE'   ? '#b45309' :
                                              '#047857';
    return `<span style="background:${tone};color:#fff;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;">${esc(narrative.verdict.replace('_', ' '))}</span>`;
  })();

  const topReports = hunt.topReports.length
    ? `<ul style="margin:0;padding-left:18px;">${hunt.topReports
        .map(r => `<li><a href="${esc(safeHref(r.url))}" rel="noopener noreferrer" style="color:#1d4ed8;">${esc(r.title)}</a> <span style="color:#64748b;">(${r.iocCount} IOC${r.iocCount === 1 ? '' : 's'} matched)</span></li>`)
        .join('')}</ul>`
    : '<p style="color:#64748b;font-style:italic;margin:0;">No linked threat reports.</p>';

  const iocChips = Object.entries(hunt.hitsByIoc)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 50)
    .map(([ioc, count]) => `<span style="display:inline-block;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;background:#fef2f2;color:#991b1b;border:1px solid #fecaca;padding:2px 8px;border-radius:6px;margin:2px 4px 2px 0;">${esc(ioc)} <span style="opacity:.7;">×${count}</span></span>`)
    .join('') || '<p style="color:#64748b;font-style:italic;margin:0;">No matched IOCs.</p>';

  const tablesRows = Object.entries(hunt.hitsByTable)
    .sort(([, a], [, b]) => b - a)
    .map(([table, count]) => {
      const raw = hunt.rawHitsByTable?.[table] ?? count;
      const rawCell = raw > count
        ? `<span style="color:#64748b;font-size:10px;">${raw.toLocaleString()}</span>`
        : `<span style="color:#cbd5e1;font-size:10px;"> - </span>`;
      return `<tr><td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-family:ui-monospace,Menlo,Consolas,monospace;">${esc(table)}</td><td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:600;">${count}</td><td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${rawCell}</td></tr>`;
    })
    .join('') || '<tr><td colspan="3" style="padding:8px;color:#64748b;font-style:italic;">No tables matched.</td></tr>';

  const matchRows = hunt.matches.slice(0, 200).map(m => {
    const occurrences = m.occurrences ?? 1;
    const firstSeen = m.firstSeen ?? m.timestamp;
    const lastSeen = m.lastSeen ?? m.timestamp;
    const fmt = (t: string) => t ? fmtTs(t) : ' - ';
    const timeCell = occurrences > 1 && lastSeen && lastSeen !== firstSeen
      ? `${esc(fmt(firstSeen))}<div style="font-size:10px;color:#94a3b8;">→ ${esc(fmt(lastSeen))}</div>`
      : esc(fmt(firstSeen));
    const tableCell = occurrences > 1
      ? `${esc(m.table)} <span style="background:#fef3c7;color:#92400e;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:700;margin-left:4px;">×${occurrences}</span>`
      : esc(m.table);
    const iocs = (m.matchedIocs ?? []).map(esc).join(', ');
    const detail = Object.entries(m.raw ?? {})
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .slice(0, 6)
      .map(([k, v]) => `${esc(k)}=${esc(typeof v === 'object' ? JSON.stringify(v) : v).slice(0, 200)}`)
      .join(' · ');
    return `<tr>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;white-space:nowrap;color:#475569;">${timeCell}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;">${tableCell}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;color:#991b1b;">${iocs}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#475569;">${detail || '<span style="opacity:.6;"> - </span>'}</td>
    </tr>`;
  }).join('');
  const matchesTruncatedNote = hunt.matches.length > 200
    ? `<p style="color:#b45309;font-size:11px;margin:8px 0 0 0;">Showing the first 200 of ${hunt.matches.length} matches${hunt.truncated ? ' (server-side truncated)' : ''}.</p>`
    : hunt.truncated
      ? `<p style="color:#b45309;font-size:11px;margin:8px 0 0 0;">Match list truncated server-side.</p>`
      : '';

  const narrativeBlock = narrative
    ? `
        ${narrative.headline ? `<h3 style="margin:0 0 6px 0;font-size:15px;color:#0f172a;">${esc(narrative.headline)}</h3>` : ''}
        ${narrative.summary ? `<p style="margin:0 0 12px 0;line-height:1.55;color:#334155;">${esc(narrative.summary)}</p>` : ''}
        ${narrative.keyFindings.length ? `
          <h4 style="margin:14px 0 6px 0;font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#475569;">Key findings</h4>
          <ul style="margin:0;padding-left:20px;color:#334155;line-height:1.55;">${narrative.keyFindings.map(f => `<li>${esc(f)}</li>`).join('')}</ul>
        ` : ''}
        ${(narrative.affectedDevices.length || narrative.affectedUsers.length) ? `
          <div style="display:flex;gap:24px;flex-wrap:wrap;margin-top:12px;">
            ${narrative.affectedDevices.length ? `<div><h4 style="margin:0 0 4px 0;font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#475569;">Affected devices</h4><div style="font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;color:#0f172a;">${narrative.affectedDevices.map(esc).join(', ')}</div></div>` : ''}
            ${narrative.affectedUsers.length ? `<div><h4 style="margin:0 0 4px 0;font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#475569;">Affected users</h4><div style="font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;color:#0f172a;">${narrative.affectedUsers.map(esc).join(', ')}</div></div>` : ''}
          </div>
        ` : ''}
        ${narrative.recommendations.length ? `
          <h4 style="margin:14px 0 6px 0;font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#475569;">Recommendations</h4>
          <ol style="margin:0;padding-left:20px;color:#334155;line-height:1.55;">${narrative.recommendations.map(r => `<li>${esc(r)}</li>`).join('')}</ol>
        ` : ''}
      `
    : `<p style="color:#64748b;font-style:italic;margin:0;">AI findings narrative was not generated yet at the time of export.</p>`;

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Kloudynet Threat Hunt - ${esc(customerName)}</title>
  <meta name="generator" content="Kloudsecure" />
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif; background:#f8fafc; color:#0f172a; margin:0; padding:32px; }
    .doc { max-width:1080px; margin:0 auto; background:#fff; border:1px solid #e2e8f0; border-radius:12px; box-shadow:0 1px 3px rgba(15,23,42,.06); }
    .header { display:flex; align-items:center; gap:16px; padding:20px 28px; border-bottom:1px solid #e2e8f0; }
    .header img { height:40px; width:auto; }
    .header .title { font-size:18px; font-weight:700; color:#0f172a; }
    .header .subtitle { font-size:12px; color:#64748b; margin-top:2px; }
    .section { padding:20px 28px; border-bottom:1px solid #e2e8f0; }
    .section:last-child { border-bottom:0; }
    .section h2 { margin:0 0 12px 0; font-size:13px; text-transform:uppercase; letter-spacing:.08em; color:#475569; }
    .stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(140px,1fr)); gap:12px; }
    .stat { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:12px; }
    .stat .label { font-size:10px; text-transform:uppercase; letter-spacing:.08em; color:#64748b; }
    .stat .value { font-size:22px; font-weight:700; color:#0f172a; margin-top:4px; }
    table { border-collapse:collapse; width:100%; font-size:12px; }
    th { text-align:left; padding:8px 10px; background:#f1f5f9; color:#475569; font-weight:600; border-bottom:1px solid #e2e8f0; font-size:11px; text-transform:uppercase; letter-spacing:.05em; }
    .footer { padding:14px 28px; font-size:10px; color:#64748b; text-align:center; }
  </style>
</head>
<body>
  <div class="doc">
    <div class="header">
      <img src="${logoDataUrl}" alt="Kloudynet" />
      <div>
        <div class="title">Kloudynet Threat Hunt Report</div>
        <div class="subtitle">${esc(customerName)} · last ${hunt.lookbackDays} days · hunt ran ${esc(ranAt)} · generated ${esc(generated)}</div>
      </div>
      <div style="margin-left:auto;">${verdictBadge}</div>
    </div>

    <div class="section">
      <h2>Summary</h2>
      <div class="stats">
        <div class="stat"><div class="label">IOCs hunted</div><div class="value">${hunt.iocsHunted}</div></div>
        <div class="stat"><div class="label">Distinct events</div><div class="value" style="color:${hunt.totalHits > 0 ? '#b91c1c' : '#047857'};">${hunt.totalHits}</div>${hunt.rawHits && hunt.rawHits > hunt.totalHits ? `<div style="font-size:10px;color:#64748b;margin-top:2px;">${hunt.rawHits.toLocaleString()} raw rows</div>` : ''}</div>
        <div class="stat"><div class="label">Tables matched</div><div class="value">${Object.keys(hunt.hitsByTable).length}</div></div>
        <div class="stat"><div class="label">Sentinel window</div><div class="value">${hunt.lookbackDays}d</div></div>
        <div class="stat"><div class="label">Query time</div><div class="value">${(hunt.durationMs / 1000).toFixed(1)}s</div></div>
      </div>
    </div>

    <div class="section">
      <h2>AI findings</h2>
      ${narrativeBlock}
    </div>

    <div class="section">
      <h2>Linked threat reports</h2>
      ${topReports}
    </div>

    <div class="section">
      <h2>IOCs that hit</h2>
      ${iocChips}
    </div>

    <div class="section">
      <h2>Hits by table</h2>
      <table>
        <thead><tr><th>Table</th><th style="text-align:right;">Distinct events</th><th style="text-align:right;">Raw rows</th></tr></thead>
        <tbody>${tablesRows}</tbody>
      </table>
    </div>

    <div class="section">
      <h2>Matches</h2>
      <table>
        <thead><tr><th>Time</th><th>Source table</th><th>Matched IOC</th><th>Detail</th></tr></thead>
        <tbody>${matchRows || '<tr><td colspan="4" style="padding:8px;color:#64748b;font-style:italic;">No matches.</td></tr>'}</tbody>
      </table>
      ${matchesTruncatedNote}
    </div>

    <div class="footer">
      Generated by Kloudsecure · Threat Intelligence · ${esc(generated)}
    </div>
  </div>
</body>
</html>`;
}
